import {Injectable} from '@angular/core';

import {PortalKey} from '../_code/portalkeys';

/**
 * Per-portal API and storage parameters.
 *
 * `portalRole` names the portal-entry role this SPA signs in against - one of the server's
 * TiberRoleNames portal roles (ManagementPortal, AdmissionsDashboard, LtiUser). The server scopes
 * the issued token to it, so a member who holds several portal roles in the database only carries
 * the one for the portal actually entered.
 *
 * `portalKey` namespaces this portal's browser storage. Every portal is served from the same
 * origin (/portal/administration/, /portal/student/, ...), so they share one sessionStorage: a
 * member who opens a second portal in the same tab would otherwise overwrite the first portal's
 * token under the shared `currentUser` key, and the surviving token would be the wrong one.
 */
@Injectable({providedIn: 'root'})
export class AppParameters {
    loginUrl = `/api/user/authenticate`;

    portalRole: string = null;

    portalKey: PortalKey = null;

    get portalLoginUrl(): string {
        return this.portalRole ? `${this.loginUrl}/${this.portalRole}` : this.loginUrl;
    }

    get tokenStorageKey(): string {
        return `currentUser:${this.portalKey || AppParameters.portalKeyFromBaseHref()}`;
    }

    /**
     * Fallback for a portal that has not set `portalKey`. Sharing a token between portals is the
     * bug this key exists to prevent, so an omission has to fail safe rather than silently fall
     * back to a shared name. The base href is the portal mount point (/portal/student/ -> student);
     * on a dev server it is `/`, but each SPA runs on its own port, so they are separate origins
     * and already have separate storage.
     */
    private static portalKeyFromBaseHref(): string {
        const base = typeof document === 'undefined' ? '/' : (document.baseURI || '/');
        const segments = base.replace(/^[a-z]+:\/\/[^/]+/i, '').split('/').filter(part => part.length > 0);

        return segments[segments.length - 1] || 'local';
    }
}

/**
 * Builds the per-portal provider value.
 *
 * Prefer this over Object.assign at each portal. Object.assign is typed (target: T, source: U) => T & U,
 * so the object literal is never checked against AppParameters - `{portalKey: 'managment'}` compiles
 * silently and the portal falls back to the base-href key at runtime. Here portalKey is a real
 * PortalKey parameter, so a typo is a compile error.
 */
export function portalParameters(portalKey: PortalKey, portalRole: string = null): AppParameters {
    return Object.assign(new AppParameters(), {portalKey, portalRole});
}
